import * as React from "react";
import { useEffect, useState, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import {
  addImageModels,
  addModel,
  getModelDetails,
  setSemanticEnabled,
} from "../../redux/slices/modelSlice";
import { getType } from "../../redux/slices/typeSlice";
import { getWebSearch } from "../../redux/slices/userSlice";
import hyperscaler_mapping from "../../data/modelMapping";
import { useTheme } from "@mui/material";

export default function SelectModels(type: any) {
  const model_user_details = useAppSelector(getModelDetails);
  const chatType = useAppSelector(getType);
  const webSearch = useAppSelector(getWebSearch);

  const theme = useTheme();
  const dispatch = useAppDispatch();

  const prevChatTypeRef = useRef<string | null>(null);
  const userSelectedRef = useRef(false);

  const [localModel, setLocalModel] = useState<string | undefined>(
    model_user_details?.model,
  );
  const [localImageModel, setLocalImageModel] = useState<string | undefined>(
    model_user_details?.imagemodel,
  );

  const semanticEnabled = Boolean(model_user_details?.semanticEnabled);
  const prevSemanticRef = useRef<boolean>(semanticEnabled);

  const SEMANTIC_ITEM = "__SEMANTIC__";

  interface Model {
    model_name: string;
    model: string;
    web_search_supported?: boolean;
  }

  // ---------- Helpers ----------
  const sortGPTfirst = (a: Model, b: Model): number => {
    const isGptA = (a?.model_name || "").toLowerCase().includes("gpt");
    const isGptB = (b?.model_name || "").toLowerCase().includes("gpt");
    if (isGptA && !isGptB) return -1;
    if (!isGptA && isGptB) return 1;
    return (a?.model_name || "").localeCompare(b?.model_name || "");
  };

  const modeKey = chatType?.mode?.[0] ?? "chat";

  const getModelsForMode = (): Model[] =>
    model_user_details?.models?.[modeKey] ?? [];

  const isWebSearchSupported = (name?: string) => {
    if (!name) return false;
    const models = getModelsForMode();
    return models.some(
      (m) => m?.model_name === name && m?.web_search_supported,
    );
  };

  const getDefaultModelName = () => {
    const models = getModelsForMode();
    const sorted = [...models].sort(sortGPTfirst);
    if (sorted.length === 0) return null;
    return sorted.length > 1 ? sorted[1].model_name : sorted[0].model_name;
  };

  // IMPORTANT: return null until models are available (prevents wrong fallback on refresh)
  const getDefaultWebSearchModelName = () => {
    const models = getModelsForMode();
    if (!models || models.length === 0) return null;

    const sorted = [...models].sort(sortGPTfirst);
    const supported = sorted.filter((m) => m?.web_search_supported);

    if (supported.length === 0) return null;

    const geminiPreferred =
      supported.find((m) =>
        (m?.model_name || "").toLowerCase().includes("gemini"),
      )?.model_name ?? supported[0].model_name;

    return geminiPreferred;
  };

  // ---------- Routing/Semantic behavior ----------
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!chatType?.type) return;

    const routingAllowed =
      chatType.type === "VANILLA LLM" || chatType.type === "DOCS";

    const chatTypeChanged = prevChatTypeRef.current !== chatType.type;

    // turn OFF semantic if chat type not allowed
    if (!routingAllowed && semanticEnabled) {
      dispatch(setSemanticEnabled(false));
    }

    // turn ON semantic only when chatType changes and webSearch is OFF
    if (routingAllowed && chatTypeChanged && !semanticEnabled && !webSearch) {
      dispatch(setSemanticEnabled(true));
    }

    prevChatTypeRef.current = chatType.type;
  }, [chatType.type, semanticEnabled, webSearch, dispatch]);

  // On first mount, enable semantic ONLY if webSearch is OFF (prevents flicker/override)
  useEffect(() => {
    if (isFirstRender.current) {
      if (!webSearch) {
        dispatch(setSemanticEnabled(true));
      }
      isFirstRender.current = false;
    }
  }, [dispatch, webSearch]);

  useEffect(() => {
    userSelectedRef.current = false;
  }, [chatType.type]);

  // ---------- Keep local mirrors in sync + initial default when no model exists ----------
  useEffect(() => {
    const isRegularModel = type.type === undefined;

    // If there is NO model selected yet, pick correct default
    if (!model_user_details?.model && !model_user_details?.imagemodel) {
      const defaultModel = webSearch
        ? getDefaultWebSearchModelName()
        : getDefaultModelName();

      if (defaultModel) {
        if (isRegularModel) {
          dispatch(addModel(defaultModel));
          setLocalModel(defaultModel);
        } else {
          dispatch(addImageModels(defaultModel));
          setLocalImageModel(defaultModel);
        }
      }
    }

    if (model_user_details?.model) setLocalModel(model_user_details.model);
    if (model_user_details?.imagemodel)
      setLocalImageModel(model_user_details.imagemodel);
  }, [
    model_user_details?.model,
    model_user_details?.imagemodel,
    model_user_details?.models,
    chatType?.mode,
    type.type,
    dispatch,
    webSearch,
  ]);

  // ---------- WebSearch ON/OFF + Refresh Fix ----------
  // This effect MUST rerun when models arrive (async), otherwise refresh stays blank.
  const prevWebSearch = useRef<boolean | null>(null);

  useEffect(() => {
    const isRegular = type.type === undefined;

    const selected = isRegular
      ? (model_user_details?.model ?? localModel)
      : (model_user_details?.imagemodel ?? localImageModel);

    const modelsForMode = getModelsForMode();
    const modelsLoaded = modelsForMode.length > 0;

    const prev = prevWebSearch.current;
    const isFirstRun = prev === null;

    // WEBSEARCH ON: set a valid supported model when:
    // - first run OR toggled ON
    // - OR selected is empty/invalid under websearch filter
    if (webSearch && modelsLoaded) {
      const wsModel = getDefaultWebSearchModelName();

      const mustFixInvalidSelection =
        webSearch &&
        modelsLoaded &&
        selected &&
        !isWebSearchSupported(selected);

      const shouldAutoselect =
        !!wsModel &&
        (isFirstRun || prev === false || !selected || mustFixInvalidSelection);

      if (webSearch && modelsLoaded && shouldAutoselect && wsModel) {
        prevSemanticRef.current = semanticEnabled;
        dispatch(setSemanticEnabled(false));

        dispatch(isRegular ? addModel(wsModel) : addImageModels(wsModel));
        isRegular ? setLocalModel(wsModel) : setLocalImageModel(wsModel);
      }
    }

    // WEBSEARCH OFF: restore routing or normal default
    if (!webSearch && prev === true) {
      if (prevSemanticRef.current) {
        dispatch(setSemanticEnabled(true));
      } else {
        const defaultModelName = getDefaultModelName();
        if (defaultModelName) {
          dispatch(
            isRegular
              ? addModel(defaultModelName)
              : addImageModels(defaultModelName),
          );
          isRegular
            ? setLocalModel(defaultModelName)
            : setLocalImageModel(defaultModelName);
        }
      }
    }

    prevWebSearch.current = webSearch;
  }, [
    webSearch,
    semanticEnabled,
    type.type,
    dispatch,
    chatType?.mode,
    model_user_details?.models, // ✅ critical for refresh (rerun when models load)
    model_user_details?.model,
    model_user_details?.imagemodel,
    localModel,
    localImageModel,
  ]);
  useEffect(() => {
    userSelectedRef.current = false;
  }, [webSearch]);
  // ---------- Current select value ----------
  const isCollection = chatType.type === "COLLECTIONS";
  const isRegularModel = type.type === undefined;

  const selectedModelValue = isRegularModel
    ? (model_user_details?.model ?? localModel ?? "")
    : (model_user_details?.imagemodel ?? localImageModel ?? "");

  const currentValue =
    !isCollection && !webSearch && semanticEnabled
      ? SEMANTIC_ITEM
      : selectedModelValue;

  // ---------- Handle change ----------
  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    userSelectedRef.current = true;

    if (value === SEMANTIC_ITEM) {
      dispatch(setSemanticEnabled(true));
      return;
    }

    dispatch(setSemanticEnabled(false));

    if (type.type === undefined) {
      dispatch(addModel(value));
      setLocalModel(value);
    } else {
      dispatch(addImageModels(value));
      setLocalImageModel(value);
    }
  };

  if (!model_user_details || !chatType.mode) {
    return <div>Error: Missing required data</div>;
  }

  const formatModelName = (rawName: string): string => {
    if (!rawName) return "";
    return rawName
      .replace(/_AI_Team|AI_Team|_team|team/gi, "")
      .replace(/[_-]+/g, " ")
      .replace(/\bgpt\b/gi, "GPT")
      .replace(/\bgemini\b/gi, "Gemini")
      .replace(/\s+/g, " ")
      .trim();
  };

  return (
    <FormControl
      variant="outlined"
      sx={{
        width: "100%",
      }}
    >
      <Select
        value={currentValue}
        onChange={handleChange}
        size="small"
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 280,
            },
          },
        }}
        sx={{
          width: "100%",
          fontSize: "11px",
          border: "1px solid #CBCBCB",
          backgroundColor: theme.palette.mode === "dark" ? "#111" : "#F3F3F3",
          borderRadius: "0px",
          textTransform: "capitalize",
          "& .MuiSelect-select": {
            display: "flex !important",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            "& > *:not(img)": {
              flex: "1",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          },
          "& .MuiPaper-rounded": {
            borderRadius: "0px",
          },
        }}
      >
        {/* Sticky Semantic option (only when webSearch is OFF and allowed types) */}
        {!webSearch &&
          (chatType?.type === "VANILLA LLM" || chatType?.type === "DOCS") && (
            <MenuItem
              value={SEMANTIC_ITEM}
              disableRipple
              disableTouchRipple
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                backgroundColor: "#FFFFFF !important",
                opacity: "1 !important",
                fontWeight: 600,
                color: "#FD5F07",
                borderBottom: "1px solid #E0E0E0",
                "&:hover": {
                  backgroundColor: "#FFFFFF !important",
                },
                "&.Mui-selected": {
                  backgroundColor: "#FFFFFF !important",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#FFFFFF !important",
                },
                "&.Mui-focusVisible": {
                  backgroundColor: "#FFFFFF !important",
                },
                "&::before, &::after": {
                  display: "none",
                },
              }}
            >
              Routing
            </MenuItem>
          )}

        {/* Model options */}
        {chatType.mode?.map((mode, i) => {
          const models =
            model_user_details?.models?.[`${mode ?? "chat"}`] || [];
          const sortedModels = [...models].sort(sortGPTfirst);

          const filteredModels = webSearch
            ? sortedModels.filter((m: any) => m?.web_search_supported)
            : sortedModels;

          return filteredModels.map((model: any, j: number) => (
            <MenuItem
              key={`${i}-${j}`}
              value={model?.model_name}
              sx={{
                "&.Mui-selected": { backgroundColor: "#FFD2B9" },
                textTransform: "capitalize",
                whiteSpace: "normal",
                wordBreak: "break-word",
                minHeight: "auto",
                padding: "8px",
              }}
            >
              <img
                src={
                  hyperscaler_mapping[
                    `${model?.model?.split("/")[0] ?? "psl"}`
                  ] || hyperscaler_mapping["default"]
                }
                alt={`${model?.model_name}`}
                width="15px"
              />
              <span>{formatModelName(model?.model_name)}</span>
            </MenuItem>
          ));
        })}
      </Select>
    </FormControl>
  );
}
